import { Chip, Theme } from '@mui/joy';
import { t } from 'i18next';

import { IAuthUser } from 'auth/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { VersionType } from 'core/enums';
import { IChipItem } from 'core/types';
import { ICustomerQuote } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

interface IVersionTypeHelper {
  determineVersionType: (isAlternateVersionEnabled: boolean | number) => VersionType;
  determineVersionTypeByUser: (user: IAuthUser | null) => VersionType;
  getVersionTypeLabelByUser: (user: IAuthUser | null) => string;
  determineVersionTypeByQuote: (quote: ICustomerQuote) => VersionType;
  getVersionTypeLabelByQuote: (quote: ICustomerQuote) => string;
  getVersionTypeLabel: (versionType: VersionType) => string;
  getVersionTypeChip: (
    theme: Theme,
    isAlternateVersionEnabled: boolean | number,
    withoutElement?: boolean
  ) => JSX.Element | IChipItem;
  getVersionTypeChipByQuote: (
    theme: Theme,
    quote: ICustomerQuote,
    withoutElement?: boolean
  ) => JSX.Element | IChipItem;
}

export const VersionTypeHelper: IVersionTypeHelper = {
  determineVersionType: (isAlternateVersionEnabled: boolean | number) => {
    return isAlternateVersionEnabled ? VersionType.ALTERNATE : VersionType.LEGACY;
  },

  determineVersionTypeByUser: (user: IAuthUser | null) => {
    return user && user.alternateVersionEnabled ? VersionType.ALTERNATE : VersionType.LEGACY;
  },

  getVersionTypeLabelByUser: (user: IAuthUser | null) => {
    const versionType = VersionTypeHelper.determineVersionTypeByUser(user);

    return VersionTypeHelper.getVersionTypeLabel(versionType);
  },

  determineVersionTypeByQuote: (quote: ICustomerQuote) => {
    return quote.alternateVersionEnabled ? VersionType.ALTERNATE : VersionType.LEGACY;
  },

  getVersionTypeLabelByQuote: (quote: ICustomerQuote) => {
    const versionType = VersionTypeHelper.determineVersionTypeByQuote(quote);

    return VersionTypeHelper.getVersionTypeLabel(versionType);
  },

  getVersionTypeLabel: (versionType: VersionType) => {
    switch (versionType) {
      case VersionType.ALTERNATE:
        return t('labelAlternateVersionType');
      case VersionType.LEGACY:
        return t('labelLegacyVersionType');
      default:
        return DEFAULT_PLACEHOLDER;
    }
  },

  getVersionTypeChip: (
    theme: Theme,
    isAlternateVersionEnabled: boolean | number,
    withoutElement = false
  ) => {
    const versionType = VersionTypeHelper.determineVersionType(isAlternateVersionEnabled);

    const chipProps = {
      label: VersionTypeHelper.getVersionTypeLabel(versionType),
      color: versionType === VersionType.ALTERNATE ? Color.PRIMARY : Color.SUCCESS,
      variant: Variant.SOFT,
      sx: {
        fontWeight: 600,
        ...(versionType === VersionType.LEGACY && {
          color: theme.palette.success[800],
          backgroundColor: theme.palette.success[200],
        }),
        ...(versionType === VersionType.ALTERNATE && {
          color: theme.palette.primary[800],
          backgroundColor: theme.palette.primary[200],
        }),
      },
    };

    if (withoutElement) {
      return chipProps;
    }

    const { label, ...rest } = chipProps;

    return <Chip {...rest}>{label}</Chip>;
  },

  getVersionTypeChipByQuote: (theme: Theme, quote: ICustomerQuote, withoutElement = false) =>
    VersionTypeHelper.getVersionTypeChip(theme, quote.alternateVersionEnabled, withoutElement),
};
